/**
 * HTTP Client with Authentication Interceptor
 * Automatically adds JWT token to requests and handles token expiration
 */

import { authService } from '../services/authService';
import { API_URL } from '../config/apiConfig';

export interface RequestConfig extends RequestInit {
  skipAuth?: boolean; // Skip adding auth header
  skipRefresh?: boolean; // Skip automatic token refresh
}

/**
 * Custom fetch wrapper with authentication interceptor
 */
export const httpClient = async (
  endpoint: string,
  config: RequestConfig = {}
): Promise<Response> => {
  const { skipAuth = false, skipRefresh = false, ...fetchConfig } = config;

  // Check if token needs refresh before making request
  if (!skipAuth && !skipRefresh && authService.shouldRefreshToken()) {
    try {
      await authService.refreshToken();
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Continue with request, will handle 401 if it fails
    }
  }

  // Prepare headers
  const headers = new Headers(fetchConfig.headers);

  // Add JWT token to Authorization header
  if (!skipAuth) {
    const token = authService.getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  // Add Content-Type if not set and body is not FormData
  // (FormData needs the browser to auto-set Content-Type with the boundary)
  if (!headers.has('Content-Type') && fetchConfig.body && !(fetchConfig.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  // Build full URL
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;

  // Make request
  const response = await fetch(url, {
    ...fetchConfig,
    headers,
  });

  // Handle 401 Unauthorized - redirect to login
  if (response.status === 401 && !skipAuth) {
    // Clear auth data
    authService.logout();

    // Only redirect if not already on login page — use replace to avoid
    // back-button loops, and defer to let React finish current render cycle
    if (!window.location.pathname.includes('/login')) {
      setTimeout(() => window.location.replace('/login'), 0);
    }

    throw new Error('Unauthorized - redirecting to login');
  }

  return response;
};

/**
 * GET request helper
 */
export const get = async (endpoint: string, config?: RequestConfig): Promise<Response> => {
  return httpClient(endpoint, {
    ...config,
    method: 'GET',
  });
};

/**
 * POST request helper
 */
export const post = async (
  endpoint: string,
  body?: any,
  config?: RequestConfig
): Promise<Response> => {
  return httpClient(endpoint, {
    ...config,
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
};

/**
 * PUT request helper
 */
export const put = async (
  endpoint: string,
  body?: any,
  config?: RequestConfig
): Promise<Response> => {
  return httpClient(endpoint, {
    ...config,
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
};

/**
 * PATCH request helper
 */
export const patch = async (
  endpoint: string,
  body?: any,
  config?: RequestConfig
): Promise<Response> => {
  return httpClient(endpoint, {
    ...config,
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
};

/**
 * DELETE request helper
 */
export const del = async (endpoint: string, config?: RequestConfig): Promise<Response> => {
  return httpClient(endpoint, {
    ...config,
    method: 'DELETE',
  });
};

export default {
  httpClient,
  get,
  post,
  put,
  patch,
  delete: del,
};
